import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import LogoPagaliImg from '../assets/logo-pagali-color@2x.png';
import AvatarPagaliImg from '../assets/img-avatar-pagali.svg';
import LoginMessages from './LoginMessages';

interface FormData {
  username: string;
  password: string;
}

interface FormErrors {
  username?: string;
  password?: string;
}

const LoginComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { showError, showSuccess } = useToast();

  // Show success message passed via navigation state (e.g. after registration)
  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      showSuccess(state.message);
      // Clear state so it doesn't re-show on refresh
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Campo obrigatório';
    }

    if (!formData.password) {
      newErrors.password = 'Campo obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (loginError) {
      setLoginError(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setLoginError(null);

    try {
      await login(formData.username, formData.password);

      // Verificar se precisa atualizar email
      const session = JSON.parse(sessionStorage.getItem('auth') || '{}');
      if (session.requiresEmailUpdate) {
        navigate('/update-email');
        return;
      }

      // Volta à página que pediu o login (ex.: pagali.cv/driver); sem ela, o estafeta vai
      // para o Pagali Driver e os restantes para o Dashboard. Antes ia sempre para o Dashboard.
      const de = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
      const destino = de?.pathname && !['/login', '/'].includes(de.pathname)
        ? `${de.pathname}${de.search || ''}`
        : session.user?.role === 'PFMOT' ? '/driver' : '/dashboard';
      if (destino === '/dashboard') sessionStorage.setItem('pageTitle', 'Dashboard');
      navigate(destino, { replace: true });
    } catch (error) {
      let message = 'Falha na autenticação. Verifique os seus dados.';
      if (error instanceof Error) {
        const errMsg = error.message.toLowerCase();
        if (errMsg.includes('análise') || errMsg.includes('aprovação') || errMsg.includes('pendente') || errMsg.includes('pending')) {
          message = 'A sua conta está em análise e ainda não foi aprovada. Receberá um email quando estiver activa.';
        } else if (errMsg.includes('inválid') || errMsg.includes('invalid') || errMsg.includes('unauthorized')) {
          message = 'Email/utilizador ou senha incorretos. Verifique os seus dados e tente novamente.';
        } else if (errMsg.includes('inativ') || errMsg.includes('inactive')) {
          message = 'A sua conta está inactiva. Contacte o suporte em suporte@pagali.cv.';
        } else if (errMsg.includes('rede') || errMsg.includes('network') || errMsg.includes('fetch')) {
          message = 'Erro de conexão. Verifique a sua internet e tente novamente.';
        } else if (errMsg.includes('timeout')) {
          message = 'O servidor demorou a responder. Tente novamente.';
        } else {
          message = error.message;
        }
      }
      setLoginError(message);
      showError(message, 'Erro de Login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand */}
      <div className={`hidden md:flex md:w-1/2 lg:w-5/12 ${process.env.REACT_APP_ENV === 'production' ? 'bg-pagali-purple' : 'bg-pagali-primary'} flex-col p-8 lg:p-12`}>
        {/* Content container with flex grow for vertical centering */}
        <div className="flex-1 flex flex-col justify-center items-center max-w-md mx-auto w-full">
          {/* Mascot - centered vertically */}
          <div className="mb-8">
            <img
              src={AvatarPagaliImg}
              alt="Pagali Avatar"
              className="w-48 lg:w-56 xl:w-64 mx-auto drop-shadow-2xl"
            />
          </div>

          {/* Login Messages Cards */}
          <div className="w-full">
            <LoginMessages />
          </div>
        </div>

        {/* Footer */}
        <p className="text-white/60 text-xs text-center mt-8">
          {new Date().getFullYear()} - Todos os direitos reservados ao Pagali
        </p>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-10">
            <img
              src={LogoPagaliImg}
              alt="Pagali logo"
              className="h-14 lg:h-16 mx-auto mb-6"
            />
            <h1 className="text-2xl lg:text-3xl font-semibold text-pagali-gray-800">
              É muito bom, rever você
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email ou Utilizador */}
            <div className="form-group">
              <label htmlFor="email" className="input-label">
                Email ou utilizador
              </label>
              <input
                id="email"
                type="text"
                value={formData.username}
                onChange={handleInputChange('username')}
                placeholder="Insira o seu email ou utilizador"
                autoComplete="username"
                className={`input-field ${
                  errors.username ? 'input-field-error' : ''
                }`}
                disabled={isLoading}
              />
              {errors.username && (
                <p className="input-error">{errors.username}</p>
              )}
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="input-label">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                placeholder="Insira sua palavra-passe"
                autoComplete="current-password"
                className={`input-field ${
                  errors.password ? 'input-field-error' : ''
                }`}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="input-error">{errors.password}</p>
              )}
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-3">
                <span className="text-lg mt-0.5">⚠️</span>
                <div>
                  <p className="font-semibold mb-0.5">Não foi possível entrar</p>
                  <p className="text-red-600">{loginError}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner-sm" />
                  Aguarde...
                </span>
              ) : (
                'Entrar'
              )}
            </button>

            {/* Forgot Password */}
            <div className="text-right">
              <Link
                to="/recover-password"
                className="text-sm text-pagali-primary hover:underline"
              >
                Esqueceu a senha? Recuperar Senha
              </Link>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-10 text-center space-y-3">
            <p className="text-pagali-gray-600">
              Não tem uma conta?{' '}
              <Link
                to="/register"
                className="text-pagali-primary font-medium hover:underline"
              >
                Criar uma conta
              </Link>
            </p>
            <p className="text-pagali-gray-600 text-sm">
              És entregador?{' '}
              <Link
                to="/driver/register"
                className="text-pagali-primary font-medium hover:underline"
              >
                Registar como Pagali Driver
              </Link>
            </p>

            <p className="text-xs text-pagali-gray-500">
              Ao criar uma conta, concorda com a nossa{' '}
              <a
                href="https://pagali.cv/landing/dist/index.html#/privacidade"
                target="_blank"
                rel="noreferrer"
                className="text-pagali-primary hover:underline"
              >
                Política de Privacidade
              </a>{' '}
              e{' '}
              <a
                href="https://pagali.cv/landing/dist/index.html#/termos"
                target="_blank"
                rel="noreferrer"
                className="text-pagali-primary hover:underline"
              >
                Termos de Uso
              </a>
              .
            </p>

            <p className="text-xs text-pagali-gray-400 mt-2">
              Protegido por reCAPTCHA —{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="hover:underline">Privacidade</a>
              {' '}e{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="hover:underline">Termos</a>
              {' '}Google aplicam-se.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;
